/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";

interface KeyValueEditorProps {
  label: string;
  initialValue: Record<string, string> | string;
  onChange: (value: Record<string, string>) => void;
  exampleKey?: string;
  exampleValue?: string;
  helperText?: string;
}

interface KeyValuePair {
  id: string;
  key: string;
  value: string;
}

const KeyValueEditor: React.FC<KeyValueEditorProps> = ({
  label,
  initialValue,
  onChange,
  exampleKey = "Key",
  exampleValue = "Value",
  helperText,
}) => {
  const { t } = useTranslation();
  const [pairs, setPairs] = useState<KeyValuePair[]>([]);

  const pairsRef = React.useRef(pairs);
  useEffect(() => {
    pairsRef.current = pairs;
  }, [pairs]);

  useEffect(() => {
    // Check if initialValue matches current state to avoid resetting distinct changes (like empty rows)
    let newRecord: Record<string, string> = {};

    // Parse initialValue into standardized record
    if (typeof initialValue === "string") {
      try {
        const parsed = JSON.parse(initialValue);
        if (
          typeof parsed === "object" &&
          parsed !== null &&
          !Array.isArray(parsed)
        ) {
          newRecord = parsed as Record<string, string>;
        }
      } catch {
        if (initialValue.trim()) {
          newRecord = { link: initialValue };
        }
      }
    } else if (initialValue && typeof initialValue === "object") {
      newRecord = initialValue;
    }

    // Convert current pairs to record
    const currentRecord: Record<string, string> = {};
    pairsRef.current.forEach((p) => {
      if (p.key.trim()) {
        currentRecord[p.key.trim()] = p.value;
      }
    });

    // Compare
    const newKeys = Object.keys(newRecord).sort();
    const currentKeys = Object.keys(currentRecord).sort();

    let isSame = newKeys.length === currentKeys.length;
    if (isSame) {
      for (let i = 0; i < newKeys.length; i++) {
        const k = newKeys[i];
        if (k !== currentKeys[i] || String(newRecord[k]) !== currentRecord[k]) {
          isSame = false;
          break;
        }
      }
    }

    if (isSame) return;

    // If different, we must reset (external update)
    const newPairsList = Object.entries(newRecord).map(([k, v]) => ({
      id: Math.random().toString(36).substr(2, 9),
      key: k,
      value: String(v),
    }));
    setPairs(newPairsList);
  }, [initialValue]);

  const notifyChange = (newPairs: KeyValuePair[]) => {
    const result: Record<string, string> = {};
    newPairs.forEach((p) => {
      if (p.key.trim()) {
        result[p.key.trim()] = p.value;
      }
    });
    onChange(result);
  };

  const handlePairChange = (
    id: string,
    field: "key" | "value",
    newValue: string
  ) => {
    const newPairs = pairs.map((p) =>
      p.id === id ? { ...p, [field]: newValue } : p
    );
    setPairs(newPairs);
    notifyChange(newPairs);
  };

  const handleAdd = () => {
    const newPairs = [
      ...pairs,
      { id: Math.random().toString(36).substr(2, 9), key: "", value: "" },
    ];
    setPairs(newPairs);
    notifyChange(newPairs);
  };

  const handleDelete = (id: string) => {
    const newPairs = pairs.filter((p) => p.id !== id);
    setPairs(newPairs);
    notifyChange(newPairs);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {label}
      </Typography>
      {helperText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          {helperText}
        </Typography>
      )}

      {pairs.map((pair, index) => (
        <Grid
          container
          spacing={1}
          key={pair.id}
          sx={{ mb: 1 }}
          alignItems="center"
        >
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={exampleKey}
              value={pair.key}
              onChange={(e) => handlePairChange(pair.id, "key", e.target.value)}
            />
          </Grid>
          <Grid item xs={7}>
            <TextField
              fullWidth
              size="small"
              placeholder={exampleValue}
              value={pair.value}
              onChange={(e) =>
                handlePairChange(pair.id, "value", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={1}>
            <IconButton
              size="small"
              onClick={() => handleDelete(pair.id)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Button
        startIcon={<AddIcon />}
        size="small"
        onClick={handleAdd}
        sx={{ mt: 1 }}
      >
        {t("common.add", "Add Item")}
      </Button>
    </Box>
  );
};

export default KeyValueEditor;
